import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Check, Loader2, Plus, Trash2, Edit, CheckCircle, XCircle, ChevronDown } from "lucide-react";
import { formatDate } from "../../utils/formatters";
import DeleteConfirmModal from "./DeleteConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Топ 20 криптовалют + стратегии
const topAssets = [
  "BTC / USDT",
  "ETH / USDT",
  "BNB / USDT",
  "XRP / USDT",
  "SOL / USDT",
  "ADA / USDT",
  "DOGE / USDT",
  "TRX / USDT",
  "AVAX / USDT",
  "LINK / USDT",
  "DOT / USDT",
  "MATIC / USDT",
  "SHIB / USDT",
  "LTC / USDT",
  "ATOM / USDT",
  "UNI / USDT",
  "XLM / USDT",
  "NEAR / USDT",
  "APT / USDT",
  "ARB / USDT",
];

const strategyPairs = [
  "LTC / ADA / SEI",
  "ETH / SOL / AVAX",
  "BTC / ETH / BNB",
  "DOGE / SHIB / PEPE",
  "DOT / ATOM / NEAR",
  "LINK / UNI / AAVE",
  "XRP / XLM / ADA",
  "SOL / APT / SUI",
  "ARB / OP / MATIC",
  "AVAX / FTM / ONE",
];

const UserManagementModal = ({ 
  user, 
  trades = [], 
  withdrawals = [], 
  deposits = [],
  onClose, 
  onUpdate,
  token 
}) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    balance: user.balance || 0,
    trading_balance: user.trading_balance || 0,
    wallet_address: user.wallet_address || ""
  });
  
  // Trade form - same as TradesTab
  const [showTradeForm, setShowTradeForm] = useState(false);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [tradeForm, setTradeForm] = useState({
    asset_name: "",
    investment: "",
    pnl_percent: "",
    status: "active",
    close_date: "",
    scheduled_date: ""
  });
  
  // PnL editing
  const [editingTradePnl, setEditingTradePnl] = useState(null);
  const [pnlForm, setPnlForm] = useState("");

  // Delete account modals
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showDeleteTrade, setShowDeleteTrade] = useState(null); // trade.id or null

  // User data
  const userTrades = trades.filter(t => t.user_id === user.id);
  const userWithdrawals = withdrawals.filter(w => w.user_id === user.id);
  const userDeposits = deposits.filter(d => d.user_id === user.id);
  
  const pendingWithdrawalAmount = userWithdrawals
    .filter(w => w.status === "pending" || w.status === "in_progress")
    .reduce((sum, w) => sum + (w.amount || 0), 0);

  const tabs = [
    { id: "profile", label: "Профиль" },
    { id: "transactions", label: "Транзакции" },
    { id: "trades", label: "Сделки" },
    { id: "scheduled", label: "Отложенные" },
    { id: "wallet", label: "Кошельки" },
    { id: "notifications", label: "Нотификации" },
  ];

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/mnro/users/${user.id}/balance`,
        { balance: parseFloat(editForm.balance), trading_balance: parseFloat(editForm.trading_balance) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (editForm.wallet_address !== (user.wallet_address || "")) {
        await axios.put(
          `${API}/mnro/users/${user.id}/wallet?wallet_address=${encodeURIComponent(editForm.wallet_address)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success("Данные сохранены");
      setEditMode(false);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setLoading(false);
    }
  };

  // Trade handlers - same as TradesTab
  const handleCreateTrade = async (e) => {
    e.preventDefault();
    if (!tradeForm.asset_name || !tradeForm.investment) {
      toast.error("Заполните обязательные поля");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/mnro/trades`,
        {
          user_id: user.id,
          asset_name: tradeForm.asset_name,
          investment: parseFloat(tradeForm.investment),
          pnl_percent: parseFloat(tradeForm.pnl_percent) || 0,
          status: tradeForm.status,
          close_date: tradeForm.close_date || null,
          scheduled_date: tradeForm.scheduled_date || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(tradeForm.status === "scheduled" ? "Отложенная сделка создана" : "Сделка создана");
      setShowTradeForm(false);
      setTradeForm({ asset_name: "", investment: "", pnl_percent: "", status: "active", close_date: "", scheduled_date: "" });
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка создания сделки");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTrade = async (tradeId) => {
    try {
      await axios.post(
        `${API}/mnro/trades/${tradeId}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Сделка закрыта");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    }
  };

  const handleDeleteTrade = async (tradeId) => {
    try {
      await axios.delete(
        `${API}/mnro/trades/${tradeId}?confirm=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Сделка удалена");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
      throw err;
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(
        `${API}/mnro/users/${user.id}?confirm=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Аккаунт удалён");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
      throw err;
    }
  };

  const handleUpdatePnl = async (tradeId) => {
    try {
      await axios.put(
        `${API}/mnro/trades/${tradeId}/pnl`,
        { pnl_percent: parseFloat(pnlForm) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("PnL обновлён");
      setEditingTradePnl(null);
      setPnlForm("");
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm("Сбросить пароль пользователя?")) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/mnro/users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Новый пароль: ${response.data.temp_password}`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async () => {
    const action = user.is_approved !== false ? "заблокировать" : "разблокировать";
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} пользователя?`)) return;
    
    setLoading(true);
    try {
      if (user.is_approved !== false) {
        await axios.post(`${API}/mnro/users/${user.id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Пользователь заблокирован");
      } else {
        await axios.post(`${API}/mnro/users/${user.id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Пользователь разблокирован");
      }
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async (title, message) => {
    if (!title || !message) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/mnro/notifications`,
        { user_id: user.id, title, message, type: "admin" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Уведомление отправлено");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#000000] border border-[#1F1F1F] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F1F1F]">
          <div className="text-[#A1A1AA] text-lg">
            Email: <span className="text-white">{user.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDeleteAccount(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[#F87171]/10 hover:bg-[#F87171]/20 border border-[#F87171]/30 text-[#F87171] text-sm rounded transition-colors"
              data-testid="delete-account-btn"
              title="Удалить аккаунт навсегда"
            >
              <Trash2 size={16} />
              Удалить аккаунт
            </button>
            <button onClick={onClose} className="text-[#A1A1AA] hover:text-white p-2" data-testid="close-modal-btn">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#1F1F1F]">
          <div className="flex px-6 gap-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-base transition-colors relative ${
                  activeTab === tab.id
                    ? "text-[#EDEDED]"
                    : "text-[#A1A1AA] hover:text-white"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div className="divide-y divide-[#1F1F1F]">
              <ProfileRow label="ID" value={user.client_id || user.id.slice(0, 8)} />
              <ProfileRow label="Email" value={user.email} />
              <ProfileRow label="Имя" value={user.name || "-"} />
              <ProfileRow 
                label="Баланс" 
                value={editMode ? (
                  <input
                    type="number"
                    value={editForm.balance}
                    onChange={(e) => setEditForm(prev => ({ ...prev, balance: e.target.value }))}
                    className="bg-[#0A0A0A] border border-white rounded px-3 py-1 text-white w-48 focus:outline-none"
                  />
                ) : (user.balance || 0).toFixed(2)}
              />
              <ProfileRow 
                label="Торговый баланс" 
                value={editMode ? (
                  <input
                    type="number"
                    value={editForm.trading_balance}
                    onChange={(e) => setEditForm(prev => ({ ...prev, trading_balance: e.target.value }))}
                    className="bg-[#0A0A0A] border border-white rounded px-3 py-1 text-white w-48 focus:outline-none"
                  />
                ) : (user.trading_balance || 0).toFixed(2)}
              />
              <ProfileRow label="На вывод" value={pendingWithdrawalAmount.toFixed(2)} />
              <ProfileRow label="Номер телефона" value={user.phone || "-"} />
              <ProfileRow 
                label="Статус" 
                value={
                  <span className={`px-3 py-1 rounded text-sm border ${
                    user.is_approved !== false
                      ? "bg-[#34D399]/20 text-[#34D399] border-[#34D399]/50"
                      : "bg-[#F87171]/20 text-[#F87171] border-[#F87171]/50"
                  }`}>
                    {user.is_approved !== false ? "✓ Активен" : "✗ Заблокирован"}
                  </span>
                }
              />
              <ProfileRow 
                label="Google Auth" 
                value={user.totp_enabled 
                  ? <span className="text-[#34D399]">✓ Активен</span>
                  : <span className="text-[#F87171]">✗ Не активен</span>
                }
              />
              <ProfileRow label="Дата регистрации" value={formatDate(user.created_at)} />
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === "transactions" && (
            <div className="p-6 space-y-8">
              <div>
                <h3 className="text-white text-lg font-medium mb-4">Выводы ({userWithdrawals.length})</h3>
                {userWithdrawals.length === 0 ? (
                  <div className="text-[#A1A1AA] text-center py-8 bg-[#0A0A0A] rounded border border-[#1F1F1F]">Нет транзакций</div>
                ) : (
                  <div className="bg-[#0A0A0A] rounded border border-[#1F1F1F] overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#1F1F1F]">
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Дата</th>
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Сумма</th>
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Сеть</th>
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userWithdrawals.map(w => (
                          <tr key={w.id} className="border-b border-[#1F1F1F]/50">
                            <td className="py-3 px-4 text-[#A1A1AA]">{formatDate(w.created_at)}</td>
                            <td className="py-3 px-4 text-[#F87171]">-${w.amount?.toFixed(2)}</td>
                            <td className="py-3 px-4 text-white">{w.asset}</td>
                            <td className="py-3 px-4">
                              <span className={w.status === "completed" ? "text-[#34D399]" : w.status === "rejected" ? "text-[#F87171]" : "text-[#EDEDED]"}>
                                {w.status === "completed" ? "Выполнен" : w.status === "rejected" ? "Отклонён" : "Ожидает"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-white text-lg font-medium mb-4">Пополнения ({userDeposits.length})</h3>
                {userDeposits.length === 0 ? (
                  <div className="text-[#A1A1AA] text-center py-8 bg-[#0A0A0A] rounded border border-[#1F1F1F]">Нет пополнений</div>
                ) : (
                  <div className="bg-[#0A0A0A] rounded border border-[#1F1F1F] overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#1F1F1F]">
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Дата</th>
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Сумма</th>
                          <th className="text-left text-[#A1A1AA] py-3 px-4 font-normal">Метод</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDeposits.map(d => (
                          <tr key={d.id} className="border-b border-[#1F1F1F]/50">
                            <td className="py-3 px-4 text-[#A1A1AA]">{formatDate(d.created_at)}</td>
                            <td className="py-3 px-4 text-[#34D399]">+${d.amount?.toFixed(2)}</td>
                            <td className="py-3 px-4 text-white">{d.method || "Admin"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Trades Tab - Same as TradesTab */}
          {activeTab === "trades" && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-white text-lg font-medium">Сделки ({userTrades.length})</h3>
                <button
                  onClick={() => setShowTradeForm(!showTradeForm)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus size={18} />
                  Создать сделку
                </button>
              </div>

              {/* Trade Form - Same as TradesTab but without user selection */}
              {showTradeForm && (
                <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-6 mb-6">
                  <h3 className="font-chivo font-bold text-white mb-4">Новая сделка</h3>
                  <form onSubmit={handleCreateTrade} className="grid md:grid-cols-2 gap-4">
                    {/* Asset Select with dropdown + manual input */}
                    <div className="relative md:col-span-2">
                      <label className="block text-[#A1A1AA] text-sm mb-2">Актив *</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={tradeForm.asset_name}
                          onChange={(e) => setTradeForm({ ...tradeForm, asset_name: e.target.value })}
                          placeholder="Введите или выберите актив..."
                          className="flex-1 bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowAssetDropdown(!showAssetDropdown)}
                          className="px-4 py-3 bg-[#000000] border border-[#1F1F1F] rounded text-[#EDEDED] hover:border-[#2E2E2E] transition-colors"
                        >
                          <ChevronDown size={20} className={`transition-transform ${showAssetDropdown ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                      {showAssetDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded z-20 max-h-80 overflow-y-auto shadow-xl">
                          <div className="p-2 border-b border-[#1F1F1F] text-[#EDEDED] text-xs font-bold">ТОП-20 КРИПТОВАЛЮТ</div>
                          {topAssets.map((asset) => (
                            <button
                              key={asset}
                              type="button"
                              onClick={() => {
                                setTradeForm({ ...tradeForm, asset_name: asset });
                                setShowAssetDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left text-white hover:bg-[#000000] transition-colors text-sm"
                            >
                              {asset}
                            </button>
                          ))}
                          <div className="p-2 border-t border-b border-[#1F1F1F] text-[#34D399] text-xs font-bold">СТРАТЕГИИ</div>
                          {strategyPairs.map((asset) => (
                            <button
                              key={asset}
                              type="button"
                              onClick={() => {
                                setTradeForm({ ...tradeForm, asset_name: asset });
                                setShowAssetDropdown(false);
                              }}
                              className="w-full px-4 py-2 text-left text-[#34D399] hover:bg-[#000000] transition-colors text-sm"
                            >
                              {asset}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={tradeForm.investment}
                      onChange={(e) => setTradeForm({ ...tradeForm, investment: e.target.value })}
                      placeholder="Сумма инвестиций ($)"
                      className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                      required
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={tradeForm.pnl_percent}
                      onChange={(e) => setTradeForm({ ...tradeForm, pnl_percent: e.target.value })}
                      placeholder="Целевой % к закрытию"
                      className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                    />
                    <select
                      value={tradeForm.status}
                      onChange={(e) => setTradeForm({ ...tradeForm, status: e.target.value })}
                      className="bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none"
                    >
                      <option value="active">Активна</option>
                      <option value="closed">Закрыта</option>
                      <option value="scheduled">Отложенная</option>
                    </select>
                    {tradeForm.status === "scheduled" && (
                      <div className="md:col-span-2">
                        <label className="block text-[#EDEDED] text-sm mb-2">Дата активации *</label>
                        <input
                          type="datetime-local"
                          value={tradeForm.scheduled_date || ""}
                          onChange={(e) => setTradeForm({ ...tradeForm, scheduled_date: e.target.value })}
                          className="w-full bg-[#000000] border border-white/50 rounded px-4 py-3 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                          required
                        />
                        <p className="text-xs text-[#A1A1AA] mt-1">Сделка автоматически активируется в это время</p>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="block text-[#A1A1AA] text-sm mb-2">Дата закрытия</label>
                      <input
                        type="datetime-local"
                        value={tradeForm.close_date}
                        onChange={(e) => setTradeForm({ ...tradeForm, close_date: e.target.value })}
                        className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none [color-scheme:dark]"
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-4">
                      <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? "Создание..." : "Создать"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTradeForm(false)}
                        className="border border-[#1F1F1F] text-white py-3 px-6 rounded hover:bg-[#0A0A0A]"
                      >
                        Отмена
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Trades Table - Same as TradesTab */}
              {userTrades.length === 0 ? (
                <div className="text-[#A1A1AA] text-center py-8 bg-[#0A0A0A] rounded border border-[#1F1F1F]">Нет сделок</div>
              ) : (
                <div className="bg-[#0A0A0A] rounded border border-[#1F1F1F] overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1F1F1F]">
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Название</th>
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Вклад/Доход</th>
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">PnL</th>
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Статус</th>
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Дата закрытия</th>
                        <th className="text-left text-[#A1A1AA] text-sm font-normal py-4 px-4">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userTrades.map((trade) => {
                        const currentPnl = trade.current_pnl_percent || 0;
                        const currentIncome = trade.current_income || 0;
                        const isPositive = currentPnl >= 0;
                        
                        return (
                          <tr key={trade.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50">
                            <td className="py-4 px-4 text-white">{trade.asset_name}</td>
                            <td className="py-4 px-4">
                              <div className="font-mono">
                                <span className="text-white">${trade.investment?.toFixed(2)}</span>
                                <span className="text-[#A1A1AA] mx-1">/</span>
                                <span className={isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}>
                                  {isPositive ? '+' : ''}${currentIncome.toFixed(2)} ({isPositive ? '+' : ''}{currentPnl.toFixed(1)}%)
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              {editingTradePnl === trade.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={pnlForm}
                                    onChange={(e) => setPnlForm(e.target.value)}
                                    className="bg-[#000000] border border-white rounded px-2 py-1 text-white w-20"
                                    placeholder="%"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleUpdatePnl(trade.id)}
                                    className="text-[#34D399] hover:text-[#34D399]/70 p-1"
                                  >
                                    <CheckCircle size={16} />
                                  </button>
                                  <button
                                    onClick={() => { setEditingTradePnl(null); setPnlForm(""); }}
                                    className="text-[#A1A1AA] hover:text-white p-1"
                                  >
                                    <XCircle size={16} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className={`font-mono ${isPositive ? 'text-[#34D399]' : 'text-[#F87171]'}`}>
                                    {isPositive ? '+' : ''}{currentPnl.toFixed(2)}%
                                  </span>
                                  {trade.status === "active" && (
                                    <button
                                      onClick={() => { setEditingTradePnl(trade.id); setPnlForm(currentPnl.toString()); }}
                                      className="text-[#EDEDED] hover:text-[#EDEDED]/70 p-1"
                                      title="Изменить PnL"
                                    >
                                      <Edit size={14} />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {trade.status === "active" ? (
                                <span className="inline-flex items-center gap-1 bg-blue-500/15 text-blue-500 px-3 py-1 rounded text-sm whitespace-nowrap">
                                  В работе
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 bg-[#34D399]/15 text-[#34D399] px-3 py-1 rounded text-sm whitespace-nowrap">
                                  Выполнено
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-4 font-mono text-[#A1A1AA] text-sm">{formatDate(trade.close_date)}</td>
                            <td className="py-4 px-4">
                              <div className="flex gap-2">
                                {trade.status === "active" && (
                                  <button
                                    onClick={() => handleCloseTrade(trade.id)}
                                    className="text-[#34D399] hover:text-[#34D399]/70 p-1"
                                    title="Закрыть"
                                  >
                                    <CheckCircle size={18} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setShowDeleteTrade(trade)}
                                  className="text-[#F87171] hover:text-[#F87171]/70 p-1"
                                  title="Удалить"
                                  data-testid={`delete-trade-${trade.id}`}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Wallet Tab */}
          {activeTab === "wallet" && (
            <div className="divide-y divide-[#1F1F1F]">
              <ProfileRow 
                label="Кошелёк для пополнения" 
                value={editMode ? (
                  <input
                    type="text"
                    value={editForm.wallet_address}
                    onChange={(e) => setEditForm(prev => ({ ...prev, wallet_address: e.target.value }))}
                    placeholder="Адрес TRC-20 кошелька"
                    className="bg-[#0A0A0A] border border-white rounded px-3 py-1 text-white w-96 focus:outline-none font-mono text-sm"
                  />
                ) : (
                  <span className="font-mono text-sm">
                    {user.wallet_address || <span className="text-[#A1A1AA] italic">Не установлен</span>}
                  </span>
                )}
              />
            </div>
          )}

          {/* Scheduled Trades Tab */}
          {activeTab === "scheduled" && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-lg font-medium">
                  Отложенные сделки ({userTrades.filter(t => t.status === "scheduled").length})
                </h3>
              </div>
              {userTrades.filter(t => t.status === "scheduled").length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-8">Нет отложенных сделок</p>
              ) : (
                <div className="space-y-3">
                  {userTrades.filter(t => t.status === "scheduled").map(trade => (
                    <div key={trade.id} className="bg-[#000000] rounded-lg p-4 border border-white/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{trade.asset_name}</span>
                        <span className="text-xs bg-white/15 text-[#EDEDED] px-2 py-1 rounded">Отложенная</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-[#A1A1AA]">Инвестиция:</span>
                          <span className="text-white ml-2">${trade.investment?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-[#A1A1AA]">PnL:</span>
                          <span className="text-white ml-2">{trade.pnl_percent}%</span>
                        </div>
                        <div>
                          <span className="text-[#A1A1AA]">Активация:</span>
                          <span className="text-[#EDEDED] ml-2">
                            {trade.scheduled_date ? new Date(trade.scheduled_date).toLocaleString("ru-RU") : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[#A1A1AA]">Закрытие:</span>
                          <span className="text-white ml-2">
                            {trade.close_date ? new Date(trade.close_date).toLocaleString("ru-RU") : "—"}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API}/mnro/trades/${trade.id}`,
                                { status: "active" },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success("Сделка активирована");
                              onUpdate();
                            } catch(e) { toast.error("Ошибка"); }
                          }}
                          className="text-xs bg-blue-500/15 text-blue-400 px-3 py-1.5 rounded hover:bg-blue-500/25 transition-colors"
                        >
                          Активировать сейчас
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await axios.delete(
                                `${API}/mnro/trades/${trade.id}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success("Сделка удалена");
                              onUpdate();
                            } catch(e) { toast.error("Ошибка"); }
                          }}
                          className="text-xs bg-red-500/15 text-red-400 px-3 py-1.5 rounded hover:bg-red-500/25 transition-colors"
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="p-6">
              <NotificationForm onSend={handleSendNotification} loading={loading} />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-center gap-4 px-6 py-4 border-t border-[#1F1F1F]">
          {editMode ? (
            <>
              <button
                onClick={() => setEditMode(false)}
                className="px-6 py-2.5 border border-[#1F1F1F] text-[#A1A1AA] rounded hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-6 py-2.5 bg-[#34D399] text-white rounded hover:bg-[#34D399]/80 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Сохранить
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="px-6 py-2.5 border-2 border-[#F87171] text-[#F87171] rounded hover:bg-[#F87171]/10 transition-colors disabled:opacity-50"
              >
                Сбросить пароль
              </button>
              <button
                onClick={handleBlockUser}
                disabled={loading}
                className="px-6 py-2.5 border-2 border-white text-[#EDEDED] rounded hover:bg-[#1A1A1A]/10 transition-colors disabled:opacity-50"
              >
                {user.is_approved !== false ? "Заблокировать" : "Разблокировать"}
              </button>
              <button
                onClick={() => setEditMode(true)}
                className="px-6 py-2.5 border-2 border-[#A1A1AA] text-white rounded hover:bg-[#1F1F1F] transition-colors"
              >
                Редактировать
              </button>
            </>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteAccount}
        onClose={() => setShowDeleteAccount(false)}
        onConfirm={handleDeleteAccount}
        title="Удалить аккаунт"
        itemType="аккаунт"
        itemName={user.email}
        warningText="Все данные (сделки, выводы, пополнения, чат, нотификации) будут удалены навсегда."
      />

      <DeleteConfirmModal
        isOpen={!!showDeleteTrade}
        onClose={() => setShowDeleteTrade(null)}
        onConfirm={() => handleDeleteTrade(showDeleteTrade.id)}
        title="Удалить сделку"
        itemType="сделку"
        itemName={showDeleteTrade ? `${showDeleteTrade.asset_name} ($${showDeleteTrade.investment})` : ""}
        warningText="Сделка будет удалена без возможности восстановления."
      />
    </div>
  );
};

// Profile row component
const ProfileRow = ({ label, value }) => (
  <div className="flex py-4 px-6">
    <div className="text-[#A1A1AA] w-48 flex-shrink-0">{label}</div>
    <div className="text-white flex-1">{value}</div>
  </div>
);

// Notification form component
const NotificationForm = ({ onSend, loading }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = () => {
    onSend(title, message);
    setTitle("");
    setMessage("");
  };

  return (
    <div>
      <h3 className="text-white text-lg font-medium mb-4">Отправить уведомление</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-[#A1A1AA] text-sm mb-2">Заголовок</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded px-4 py-2 text-white focus:border-white focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-[#A1A1AA] text-sm mb-2">Сообщение</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full bg-[#0A0A0A] border border-[#1F1F1F] rounded px-4 py-2 text-white focus:border-white focus:outline-none resize-none"
          />
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading || !title || !message}
          className={`px-6 py-2 rounded font-bold transition-colors ${
            title && message
              ? "bg-[#FF8A1E] text-white hover:bg-[#E5E5E5]"
              : "bg-[#1F1F1F] text-[#71717A] cursor-not-allowed"
          }`}
        >
          Отправить
        </button>
      </div>
    </div>
  );
};

export default UserManagementModal;
